/**
 * Tenant-facing price display. `1 DDT = $1.00` and `1 DDT = 1,000,000 uddt`,
 * so a `uddt` amount is already a microdollar amount: the conversion is an
 * exact unit transformation and never an exchange-rate lookup.
 *
 * All arithmetic is `bigint`. A chain price is an integer per block; turning
 * it into a per-hour figure through `Number` would round a real invoice.
 */

/** Microdollars in one displayed dollar. `1 DDT = $1.00 = 1_000_000 uddt`. */
const MICRO_PER_DOLLAR = 1_000_000n

/** The only denomination a tenant price may be quoted in. */
const TENANT_DENOM = 'uddt'

const SECONDS_PER_HOUR = 3600

/**
 * Seconds per block. Not in the SDL, so it is a parameter; ~6s is the upstream
 * Akash norm.
 *
 * It lives here rather than beside the SDL parser because every price display
 * needs it, and importing it from there dragged the YAML parser into the
 * initial bundle.
 */
export const BLOCK_SECONDS = 6

/** Minimum fractional digits shown, so `$1` renders as `$1.00`. */
const MIN_FRACTION_DIGITS = 2

export interface PriceCoin {
  denom: string
  amount: string
}

/**
 * The maximum fractional digits a chain amount may carry.
 *
 * Cosmos decimal values are fixed-point with eighteen places, so a gateway or
 * chain read can hand this console `"25"` and `"25.000000000000000000"` for
 * the same price. Both are the same integer amount and both must display.
 */
const CHAIN_AMOUNT_SCALE = 18

/**
 * Exact integer micro-amount for a chain amount string, or `null`.
 *
 * Accepts an integer (`"25"`) or a fixed-point-18 decimal (`"25.000000000000000000"`)
 * and returns the same `bigint` for both. A decimal whose fractional part is
 * NOT zero is rejected rather than truncated: the denomination is already the
 * smallest unit the chain has, so a leftover fraction means the value is not
 * one this console can display exactly, and rounding it would show a tenant a
 * price they were never quoted.
 */
export function parseChainAmount(amount: unknown): bigint | null {
  if (typeof amount !== 'string') return null
  // Not trimmed: surrounding whitespace means the value did not come through
  // the wire shape this console expects, and guessing at it is how a stray
  // character becomes a displayed price.
  const raw = amount
  if (/^\d+$/.test(raw)) return BigInt(raw)
  const decimal = /^(\d+)\.(\d+)$/.exec(raw)
  if (!decimal) return null
  const fraction = decimal[2]!
  if (fraction.length > CHAIN_AMOUNT_SCALE) return null
  // A non-zero remainder is a value below the chain's own smallest unit.
  if (/[1-9]/.test(fraction)) return null
  return BigInt(decimal[1]!)
}

/**
 * The exact micro-amount of a coin quoted in the tenant denomination, or
 * `null` for any other denomination or unreadable amount.
 */
export function tenantMicros(coin: PriceCoin | null | undefined): bigint | null {
  if (!coin || coin.denom !== TENANT_DENOM) return null
  return parseChainAmount(coin.amount)
}


/**
 * Exact microdollars per hour for a `uddt`-per-block price, or `null` when the
 * input is not a price this function is allowed to display.
 *
 * `null` is deliberate: an unreadable price must render as unavailable, never
 * as a confident `$0.00/hour`.
 */
export function usdMicrosPerHour(
  price: PriceCoin | null | undefined,
  blockSeconds: number,
): bigint | null {
  const perBlock = tenantMicros(price)
  if (perBlock === null) return null
  if (!Number.isInteger(blockSeconds) || blockSeconds <= 0) return null
  if (SECONDS_PER_HOUR % blockSeconds !== 0) return null

  const blocksPerHour = BigInt(SECONDS_PER_HOUR / blockSeconds)
  return perBlock * blocksPerHour
}

/** Render exact microdollars as `$…`, with 2–6 fractional digits. */
export function formatUsdMicros(micros: bigint): string {
  const whole = micros / MICRO_PER_DOLLAR
  const frac = (micros % MICRO_PER_DOLLAR).toString().padStart(6, '0')
  // Keep every significant digit up to six, but never fewer than two.
  const trimmed = frac.replace(/0+$/, '')
  const digits = trimmed.length > MIN_FRACTION_DIGITS ? trimmed : frac.slice(0, MIN_FRACTION_DIGITS)
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `$${grouped}.${digits}`
}

/**
 * Render an exact hourly price as the plain decimal value accepted by the
 * guided form (for example, `15000 uddt/hour` becomes `0.015`).
 *
 * The provider's reference price is an integer per block. Keeping this
 * conversion beside the dollar formatter makes the read-only posted-price
 * field and the catalog use the same denomination, cadence and bigint
 * arithmetic, without parsing a formatted `$…/hour` label back into a number.
 */
export function usdDdtPerHour(
  price: PriceCoin | null | undefined,
  blockSeconds: number,
): string | null {
  const micros = usdMicrosPerHour(price, blockSeconds)
  if (micros === null) return null
  const decimal = formatUsdMicros(micros).slice(1).replaceAll(',', '')
  return decimal.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '')
}

/**
 * Render a wallet balance as dollars when it is the tenant's exact `uddt`
 * denomination. Balances use the same one-to-one microdollar conversion as
 * prices; malformed or unsupported coins stay unavailable instead of being
 * presented as a misleading zero.
 */
export function formatUsdBalance(coin: PriceCoin | null | undefined): string | null {
  // The tenant chain currently defines uddt as the exact microdollar unit;
  // reject other denominations rather than implying an exchange rate.
  const micros = tenantMicros(coin)
  return micros === null ? null : formatUsdMicros(micros)
}

/**
 * Render a balance for the fixed-width topbar slot as dollars and cents.
 *
 * The topbar cannot absorb an unbounded fractional figure, and the exact
 * balance remains available through `formatUsdBalance` in the account menu.
 */
export function formatUsdBalanceCompact(coin: PriceCoin | null | undefined): string | null {
  const micros = tenantMicros(coin)
  if (micros === null) return null
  const cents = micros / 10_000n
  const whole = (cents / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `$${whole}.${(cents % 100n).toString().padStart(2, '0')}`
}

/**
 * `formatUsdPerHour({ denom: 'uddt', amount: '25' }, 6)` → `'$0.015/hour'`.
 *
 * Returns `null` for any price or block interval this cannot convert exactly.
 */
export function formatUsdPerHour(
  price: PriceCoin | null | undefined,
  blockSeconds: number,
): string | null {
  const micros = usdMicrosPerHour(price, blockSeconds)
  if (micros === null) return null
  return `${formatUsdMicros(micros)}/hour`
}

/** Render an already-exact hourly micro-amount as `$…/hour`. */
export function formatUsdMicrosPerHour(micros: bigint): string {
  return `${formatUsdMicros(micros)}/hour`
}

/** One provider's indicative price, as the catalog publishes it. */
export interface ObservedPrice {
  denom: string
  amountPerBlock: string
  blockSeconds: number | null
}

/**
 * The lowest hourly price any provider has been observed publishing, or `null`
 * when none of them is readable.
 *
 * Used where a tenant is waiting on offers that have not arrived: a market with
 * no bids in it is still a market with prices in it, and the lowest observed
 * one tells the tenant whether their maximum is plausible. It binds nobody,
 * so every caller must label it as indicative.
 */
export function lowestObservedMicrosPerHour(prices: readonly ObservedPrice[]): bigint | null {
  let lowest: bigint | null = null
  for (const price of prices) {
    const micros = usdMicrosPerHour(
      { denom: price.denom, amount: price.amountPerBlock },
      price.blockSeconds ?? BLOCK_SECONDS,
    )
    if (micros === null) continue
    if (lowest === null || micros < lowest) lowest = micros
  }
  return lowest
}
